import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {
  FiHome,
  FiFileText,
  FiUsers,
  FiBarChart2,
  FiPackage,
  FiImage,
  FiEdit,
  FiSettings,
  FiUser,
  FiMenu,
  FiX,
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight
} = FiIcons;

// Mobile detection utility
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  // కొత్తగా జోడించినది: అప్లికేషన్ డేటా వచ్చినప్పుడు, దాన్ని వచ్చినప్పుడు చేయండి
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [activeServices, setActiveServices] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // కొత్తగా జోడించినది: రియల్-టైమ్ అప్‌డేట్‌లు మరియు డేటాను ఫెచ్ చేయడానికి useEffect హుక్
  useEffect(() => {
    // డేటాబేస్ నుండి అప్లికేషన్‌లను మొదట ఫెచ్ చేయండి
    const fetchApplications = async () => {
      const { data, error } = await supabase
        .from('available_orders_a7b5c9')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching applications:', error);
      } else {
        setApplications(data || []);
      }
      setLoading(false);
    };

    fetchApplications();

    // రియల్-టైమ్ సబ్‌స్క్రిప్షన్‌ను ఏర్పాటు చేయడం
    const channel = supabase
      .channel('public:available_orders_a7b5c9')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'available_orders_a7b5c9' },
        (payload) => {
          // కొత్త అప్లికేషన్ వచ్చినప్పుడు, దాన్ని జాబితాలో చేర్చండి
          setApplications((prevApplications) => [payload.new, ...prevApplications]);
        }
      )
      .subscribe();

    // కాంపోనెంట్ అన్‌మౌంట్ అయినప్పుడు సబ్‌స్క్రిప్షన్‌ను క్లీన్ చేయండి
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Fetch dynamic stats: total users, revenue, active services
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        // Total users = customers + operators
        const [custRes, operRes] = await Promise.all([
          supabase.from('customers_h2a').select('*', { count: 'exact', head: true }),
          supabase.from('operators_a7b5c9').select('*', { count: 'exact', head: true })
        ]);
        const custCount = custRes.count || 0;
        const operCount = operRes.count || 0;
        setTotalUsers(custCount + operCount);

        // Revenue = sum of completed payments
        const { data: payments, error: payErr } = await supabase
          .from('payment_qr_codes_f7k3lp')
          .select('amount, payment_status');
        if (payErr) {
          console.error('Error fetching payments:', payErr);
        }
        const revenueSum = Array.isArray(payments)
          ? payments
              .filter(p => (p.payment_status || '').toLowerCase() === 'completed')
              .reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
          : 0;
        setRevenue(revenueSum);

        // Active services = try count where is_active=true, fallback to total count
        let activeCount = 0;
        try {
          const activeRes = await supabase
            .from('services_dy3xr')
            .select('*', { count: 'exact', head: true })
            .eq('is_active', true);
          activeCount = activeRes.count || 0;
          if (!activeCount) {
            const totalRes = await supabase
              .from('services_dy3xr')
              .select('*', { count: 'exact', head: true });
            activeCount = totalRes.count || 0;
          }
        } catch (svcErr) {
          console.warn('Active services count fallback due to error:', svcErr);
          const totalRes = await supabase
            .from('services_dy3xr')
            .select('*', { count: 'exact', head: true });
          activeCount = totalRes.count || 0;
        }
        setActiveServices(activeCount);
      } catch (e) {
        console.error('Failed to fetch admin stats:', e);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: FiHome },
    { path: '/admin/verify-applications', label: 'Verify Applications', icon: FiFileText },
    { path: '/admin/operators', label: 'Manage Operators', icon: FiUsers },
    { path: '/admin/revenue-reports', label: 'Revenue Reports', icon: FiBarChart2 },
    { path: '/admin/membership-plans', label: 'Membership Plans', icon: FiPackage },
    { path: '/admin/promotions', label: 'Promotions', icon: FiImage },
    { path: '/admin/site-content', label: 'Site Content', icon: FiEdit },
    { path: '/admin/service-links', label: 'Service Management', icon: FiSettings },
    { path: '/admin/admin-user', label: 'Admin User', icon: FiUser },
  ];

  const isActivePath = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const totalPages = Math.max(1, Math.ceil((applications?.length || 0) / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedApplications = applications.slice(startIdx, endIdx);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [applications, totalPages, currentPage]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActivePath(item.path)
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <SafeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className='w-full'>
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={FiMenu} className="w-6 h-6" />
            </button>
            <div className="flex-1" />
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">Welcome, Admin</span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="p-4 sm:p-6 lg:p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Mobile Admin Restriction Notice */}
            {isMobile && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Admin Panel Not Available on Mobile
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      For security and better user experience, admin functionality is only available on desktop devices. Please access this page from a computer or tablet.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-2">Welcome to the admin panel</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{statsLoading ? '...' : totalUsers}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-full">
                    <SafeIcon icon={FiFileText} className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Applications</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {loading ? '...' : applications.length}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 rounded-full">
                    <SafeIcon icon={FiBarChart2} className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Revenue</p>
                    <p className="text-2xl font-semibold text-gray-900">{statsLoading ? '...' : `₹${revenue}`}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <SafeIcon icon={FiSettings} className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Services</p>
                    <p className="text-2xl font-semibold text-gray-900">{statsLoading ? '...' : activeServices}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Applications List */}
            <Card className="p-6 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">New Applications ({applications.length})</h3>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Fetching applications...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.length > 0 ? (
                    <>
                      {paginatedApplications.map((app) => (
                        <div key={app.id} className="p-4 border border-gray-200 rounded-lg shadow-sm bg-gray-50 flex justify-between items-center">
                          <div>
                            <p className="font-bold text-lg text-gray-800">{app.customer_name}</p>
                            <p className="text-sm text-gray-500">{app.service_type}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Submitted at: {new Date(app.created_at).toLocaleString()}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${app.status === 'available' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {app.status}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-4">
                        <p className="text-sm text-gray-600">
                          Showing {applications.length === 0 ? 0 : startIdx + 1}–{Math.min(endIdx, applications.length)} of {applications.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          >
                            <div className="flex items-center">
                              <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
                              Prev
                            </div>
                          </Button>
                          <span className="text-sm text-gray-700">
                            Page {currentPage} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          >
                            <div className="flex items-center">
                              Next
                              <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
                            </div>
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No new applications to display.</p>
                  )}
                </div>
              )}
            </Card>

            {/* Quick Actions */}
            <Card className="p-6 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex gap-4 overflow-x-auto">
                {/* <Link to="admin/service-links">
                  <Button variant="outline" className="flex flex-shrink-0">
                    <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2" />
                    Manage Services
                  </Button>
                </Link> */}
                <Link to="/admin/verify-applications">
                  <Button variant="outline" className="flex flex-shrink-0">
                    <SafeIcon icon={FiFileText} className="w-5 h-5 mr-2" />
                    Verify Applications
                  </Button>
                </Link>
                <Link to="/admin/operators">
                  <Button variant="outline" className="flex flex-shrink-0">
                    <SafeIcon icon={FiUsers} className="w-5 h-5 mr-2" />
                    Manage Operators
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;